import React, { useState, useContext, useEffect, useMemo } from 'react';
import { makeStyles, Grid, Tabs, Tab, CircularProgress } from '@material-ui/core';

import { FormattedMessage, injectIntl } from 'react-intl';

import { Formik, Form } from 'formik';
import { FormServiceContext } from 'app/utils/contexts';

import Request from './components/Request';
import Documentation from 'app/components/Documentation';
import AlertMessage from 'app/components/AlertMessage';
import RegistrationStatus from '../../Registration/components/RegistrationStatus';
import SaveButton from 'app/components/SaveButton';
import FinalizeButton from 'app/components/FinalizeButton';

import { fetchCities, fetchPolos } from 'app/store/slices/forms';
import { useDispatch, useSelector } from 'react-redux';
import notification from 'app/config/notification';
import {
    fetchVehicleTypes,
    fetchVehicleCategories,
    fetchInspectedVehicles,
    fetchInspectedVehiclesByDocument,
    fetchAccessibilityType,
    fetchVehicleModels,
    fetchVehicleBrands,
    fetchFuelType,
    fetchVehicleClassification,
    fetchSubSystems,
    fetchLicenseVehicleDocuments,
} from 'app/store/slices/vehicle';

import { STATE_CITY } from 'app/utils/constants';

import styles from './styles';
const useStyles = makeStyles(styles);

/**
 * Componente LicenseRegistration para gerenciar solicitações de licenças.
 *
 * @param {object} props - Propriedades do componente.
 * @param {object} props.intl - Objeto de internacionalização.
 * @param {boolean} props.loadingDocuments - Flag indicando se os documentos estão sendo carregados.
 * @param {function} props.setLoadingDocuments - Função para definir a flag de carregamento de documentos.
 * @param {boolean} props.canEdit - Flag indicando se a solicitação pode ser editada.
 * @param {boolean} props.editing - Flag indicando se a solicitação está sendo editada.
 * @param {function} props.resetAndGo - Função para redefinir o formulário e navegar para uma rota específica.
 * @param {function} props.onCancel - Função para cancelar a solicitação.
 * @param {function} props.validateForm - Função para validar o formulário.
 * @param {object} props.breadCrumb - Objeto de breadcrumb.
 * @param {object} props.rightInfo - Objeto de informações à direita.
 * @returns {JSX.Element} - Componente LicenseRegistration.
 */
function LicenseRegistration(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const viewsIdentification = 0;
    const viewsDocumentation = 1;

    const [changingDate, setChangingDate] = useState(0);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(viewsIdentification);

    const contextProps = useContext(FormServiceContext);

    const polos = useSelector((state) => state.forms.polos);
    const cities = useSelector((state) => state.forms.cities);
    const inspectedVehicles = useSelector((state) => state.vehicle.inspectedVehicles);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const vehicleCategories = useSelector((state) => state.vehicle.vehicleCategories);
    const accessibilityType = useSelector((state) => state.vehicle.accessibilityType);
    const fuelType = useSelector((state) => state.vehicle.fuelType);
    const vehicleClassification = useSelector((state) => state.vehicle.vehicleClassification);
    const vehicleModels = useSelector((state) => state.vehicle.vehicleModels);
    const vehicleBrands = useSelector((state) => state.vehicle.vehicleBrands);
    const subSystems = useSelector((state) => state.vehicle.subSystems);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);

    const service = useSelector((state) => state.services.details);

    const request = useSelector((state) => state.requests.details);
    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    const {
        initialValues,
        validationSchema,
        validateForm,
        validateOnSave,
        onSubmit,
        onSave,
        onCancel,
        mainColor,
        hideActionBtn,
        hideFinalizeBtn,
        canEdit,
        isInternalForm,
        loadingDocuments,
    } = useContext(FormServiceContext);

    const handleViewChange = (event, value) => {
        setView(value);
    };

    const onChangeDate = (values, field, value) => {
        setChangingDate(changingDate + 1);
        values[field] = value;
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const promisses = [];

            if (Object.keys(internalNewRequest).length > 0) {
                let baseData = { ...internalNewRequest.user };
                if (internalNewRequest.company) {
                    baseData = { ...internalNewRequest.company };
                }
                const document = baseData.deCpfCnpj || baseData.deCnpjMatriz;
                promisses.push(dispatch(fetchInspectedVehiclesByDocument(document)));
            } else if (isInternalForm) {
                const document = requestJsonData.deCpfCnpj || request.cdUsuarioSolicitacao;
                promisses.push(dispatch(fetchInspectedVehiclesByDocument(document)));
            } else {
                if (inspectedVehicles.length === 0) {
                    promisses.push(dispatch(fetchInspectedVehicles()));
                }
            }

            promisses.push(dispatch(fetchLicenseVehicleDocuments(service.cdServico)));

            if (polos.length === 0) promisses.push(dispatch(fetchPolos()));
            if (cities.length === 0) promisses.push(dispatch(fetchCities(STATE_CITY)));
            if (vehicleType.length === 0) promisses.push(dispatch(fetchVehicleTypes()));
            if (vehicleCategories.length === 0) promisses.push(dispatch(fetchVehicleCategories()));
            if (accessibilityType.length === 0) promisses.push(dispatch(fetchAccessibilityType()));
            if (vehicleModels.length === 0) promisses.push(dispatch(fetchVehicleModels()));
            if (vehicleBrands.length === 0) promisses.push(dispatch(fetchVehicleBrands()));
            if (fuelType.length === 0) promisses.push(dispatch(fetchFuelType()));
            if (vehicleClassification.length === 0) promisses.push(dispatch(fetchVehicleClassification()));
            if (subSystems.length === 0) promisses.push(dispatch(fetchSubSystems()));
            if (promisses.length > 0) await Promise.all(promisses);
            setLoading(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    if (loading) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className={classes.centerProgress}>
                        <CircularProgress size={20} />
                    </div>
                </Grid>
            </Grid>
        );
    }

    const renderTabs = () => {
        return (
            <Grid container spacing={3} className={classes.tabsContainer}>
                <Tabs
                    variant="scrollable"
                    onChange={handleViewChange}
                    className={classes.tabs}
                    indicatorColor={mainColor ? mainColor : 'primary'}
                    textColor={mainColor ? mainColor : 'primary'}
                    value={view}>
                    <Tab className={classes.tab} label={<FormattedMessage id="LABELS.SERVICES.REQUEST" />} />
                    {loadingDocuments && !isInternalForm && (
                        <Tab
                            className={classes.tab}
                            label={<CircularProgress size={12} className={classes.loading} />}
                        />
                    )}
                    {!isInternalForm && !loadingDocuments && (
                        <Tab className={classes.tab} label={<FormattedMessage id="LABELS.COMMONS.DOCUMENTATION" />} />
                    )}
                </Tabs>
            </Grid>
        );
    };

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues ? initialValues : {}}
                validationSchema={validationSchema}
                // validateOnBlur={false}
                // validateOnChange={false}
                onSubmit={(values) => onSubmit(values)}>
                {(props) => {
                    return (
                        <Form className={classes.containerForm}>
                            {renderTabs()}

                            <AlertMessage isInternalForm={isInternalForm} />

                            <Grid container spacing={3} className={classes.containerMainData}>
                                <RegistrationStatus {...props} classes={classes} />
                            </Grid>

                            {view === viewsIdentification && (
                                <Request {...props} contextProps={contextProps} onChangeDate={onChangeDate} />
                            )}
                            {view === viewsDocumentation && <Documentation {...props} contextProps={contextProps} />}

                            <SaveButton
                                mainColor={mainColor}
                                hideActionBtn={hideActionBtn}
                                isInternalForm={isInternalForm}
                                canEdit={canEdit}
                                onCancel={onCancel}
                                onSave={() => {
                                    if (validateOnSave) {
                                        props.validateForm().then((res) => {
                                            if (Object.keys(res).length <= 0) onSave(props.values);
                                            else {
                                                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                    toastId: 'VALIDATION',
                                                    type: 'error',
                                                });
                                            }
                                        });
                                    } else {
                                        onSave(props.values);
                                    }
                                }}
                            />

                            <FinalizeButton
                                mainColor={mainColor}
                                hideFinalizeBtn={hideFinalizeBtn}
                                isInternalForm={isInternalForm}
                                canEdit={canEdit}
                                onSubmit={() => {
                                    validateForm && validateForm(props.validateForm());
                                }}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
}

export default injectIntl(LicenseRegistration);
